import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Collapse,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import HistoryIcon from "@mui/icons-material/History";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getAuthToken } from "../../utils/getAuthToken";
import { useSelector } from "react-redux";
import useContactFieldOptions from "../../hooks/useContactFieldOptions";

const API_BASE = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

const SOURCE_TYPE_OPTIONS = [
  { value: "catalog", label: "From Order" },
  { value: "contact", label: "From Contact" },
  { value: "ask_customer", label: "Ask Customer" },
  { value: "fixed", label: "Fixed Value" },
  { value: "generated", label: "Auto Generate" },
];

// Only offered when this action lives in a "Send Template" trigger workflow (see
// templateVariableFields prop, threaded down from ChooseActionDrawer.jsx) — a plain "From
// Order"/"From Contact" style field doesn't apply outside that context.
const TEMPLATE_SOURCE_TYPE_OPTIONS = [
  { value: "quick_reply_button", label: "Quick Reply Button" },
  { value: "template_variable", label: "From Template" },
];

const ORDER_FIELDS = [
  { value: "price", label: "Total price", numeric: true },
  { value: "currency", label: "Currency" },
  { value: "quantity", label: "Quantity", numeric: true },
  { value: "productId", label: "Product ID" },
  { value: "title", label: "Product title" },
  { value: "description", label: "Description" },
  { value: "catalogId", label: "Catalog ID" },
  { value: "totalItems", label: "Total items", numeric: true },
  { value: "productName", label: "Product name" },
];

const OUTPUT_DATA_TYPE_OPTIONS = [
  { value: "", label: "String (default)" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
];

const FIXED_DATA_TYPE_OPTIONS = [
  { value: "string", label: "String" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "array", label: "Array" },
];

const ARRAY_ITEM_TYPE_OPTIONS = [
  { value: "string", label: "String" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
];

const VALIDATION_OPTIONS = [
  { value: "", label: "None" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "text", label: "Text" },
];

const GENERATED_OPTIONS = [
  { value: "uuid", label: "UUID" },
  { value: "timestamp", label: "Timestamp" },
];

const LINE_ITEM_ORDER_FIELDS = [
  { value: "product_retailer_id", label: "Product ID" },

  { value: "name", label: "Product name" },
  { value: "item_price", label: "Price" },
  { value: "quantity", label: "Quantity" },
  { value: "currency", label: "Currency" },
];

const LINE_ITEM_SOURCE_OPTIONS = [
  { value: "catalog", label: "From order" },
  { value: "fixed", label: "Fixed value" },
];

/** One row under Map order products: API field + catalog vs fixed + map or typed value */
function LineItemColumnRow({ col, onPatch, onRemove }) {
  const sourceType = col.sourceType === "fixed" ? "fixed" : "catalog";
  const isFixed = sourceType === "fixed";
  const lineItemFixedTypes = FIXED_DATA_TYPE_OPTIONS.filter((o) => o.value !== "array");

  return (
    <TableRow>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          label="API field"
          placeholder="e.g. item_id"
          value={col.apiField || ""}
          onChange={(e) => onPatch({ apiField: e.target.value })}
        />
      </TableCell>
      <TableCell sx={{ minWidth: 130 }}>
        <FormControl size="small" fullWidth>
          <InputLabel>Source</InputLabel>
          <Select
            label="Source"
            value={sourceType}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "fixed") {
                onPatch({ sourceType: "fixed", mapFrom: "" });
              } else {
                onPatch({
                  sourceType: "catalog",
                  fixedValue: "",
                  fixedDataType: "string",
                });
              }
            }}
          >
            {LINE_ITEM_SOURCE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell sx={{ minWidth: 260 }}>
        {!isFixed ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <FormControl size="small" fullWidth>
              <Select
                value={col.mapFrom || ""}
                onChange={(e) => onPatch({ mapFrom: e.target.value })}
                displayEmpty
              >
                <MenuItem value="">Select…</MenuItem>
                {LINE_ITEM_ORDER_FIELDS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Value type</InputLabel>
              <Select
                label="Value type"
                value={col.outputDataType || ""}
                onChange={(e) => onPatch({ outputDataType: e.target.value || undefined })}
              >
                <MenuItem value="">Auto</MenuItem>
                <MenuItem value="string">String</MenuItem>
                <MenuItem value="number">Number</MenuItem>
              </Select>
            </FormControl>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Value type</InputLabel>
              <Select
                label="Value type"
                value={col.fixedDataType || "string"}
                onChange={(e) => {
                  const t = e.target.value;
                  onPatch({
                    fixedDataType: t,
                    fixedValue: t === "boolean" ? "false" : "",
                  });
                }}
              >
                {lineItemFixedTypes.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(col.fixedDataType || "string") === "boolean" ? (
              <FormControl size="small" fullWidth>
                <Select
                  value={col.fixedValue === true || col.fixedValue === "true" ? "true" : "false"}
                  onChange={(e) => onPatch({ fixedValue: e.target.value })}
                >
                  <MenuItem value="true">true</MenuItem>
                  <MenuItem value="false">false</MenuItem>
                </Select>
              </FormControl>
            ) : (col.fixedDataType || "string") === "number" ? (
              <TextField
                size="small"
                fullWidth
                type="number"
                placeholder="Number"
                value={col.fixedValue === 0 || col.fixedValue === "0" ? col.fixedValue : col.fixedValue ?? ""}
                onChange={(e) => onPatch({ fixedValue: e.target.value })}
              />
            ) : (
              <TextField
                size="small"
                fullWidth
                placeholder="Text (empty allowed)"
                value={col.fixedValue != null ? String(col.fixedValue) : ""}
                onChange={(e) => onPatch({ fixedValue: e.target.value })}
              />
            )}
          </Box>
        )}
      </TableCell>
      <TableCell padding="checkbox">
        <IconButton size="small" onClick={onRemove} aria-label="Remove column">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

function FieldRow({ field, onChange, onRemove, showRemove, showOrderFields = true, templateVariableFields = null }) {
  const isCatalog = field.sourceType === "catalog";
  const contactFieldOptions = useContactFieldOptions();
  const isContact = field.sourceType === "contact";
  const isAskCustomer = field.sourceType === "ask_customer";
  const isFixed = field.sourceType === "fixed";
  const isGenerated = field.sourceType === "generated";
  const isQuickReplyButton = field.sourceType === "quick_reply_button";
  const isTemplateVariable = field.sourceType === "template_variable";
  const filteredSourceOptions = [
    ...(showOrderFields ? SOURCE_TYPE_OPTIONS : SOURCE_TYPE_OPTIONS.filter((o) => o.value !== "catalog")),
    ...(templateVariableFields ? TEMPLATE_SOURCE_TYPE_OPTIONS : []),
  ];

  return (
    <TableRow>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          placeholder="e.g. email"
          value={field.fieldName || ""}
          onChange={(e) => onChange("fieldName", e.target.value)}
          label="API field name"
        />
      </TableCell>
      <TableCell>
        <FormControl size="small" fullWidth>
          <Select
            value={!showOrderFields && (field.sourceType || "catalog") === "catalog" ? "ask_customer" : (field.sourceType || "catalog")}
            onChange={(e) => onChange("sourceType", e.target.value)}
          >
            {filteredSourceOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        {isCatalog && (
          <FormControl size="small" fullWidth>
            <Select
              value={field.sourceKey || ""}
              onChange={(e) => onChange("sourceKey", e.target.value)}
              displayEmpty
            >
              <MenuItem value="">Select order field…</MenuItem>
              {ORDER_FIELDS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {isContact && (
          <FormControl size="small" fullWidth>
            <Select
              value={field.sourceKey || ""}
              onChange={(e) => onChange("sourceKey", e.target.value)}
              displayEmpty
            >
              <MenuItem value="">Select contact field…</MenuItem>
              {contactFieldOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {isQuickReplyButton && (
          <Typography variant="caption" color="text.secondary">
            Uses the quick-reply button the contact tapped to reach this branch.
          </Typography>
        )}
        {isTemplateVariable && (
          <FormControl size="small" fullWidth>
            <Select
              value={field.sourceKey || ""}
              onChange={(e) => onChange("sourceKey", e.target.value)}
              displayEmpty
            >
              <MenuItem value="">Select template variable…</MenuItem>
              {(templateVariableFields || []).map((v) => (
                <MenuItem key={v.name} value={v.name}>
                  {v.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {isAskCustomer && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="e.g. Please enter your email"
              value={field.question || ""}
              onChange={(e) => onChange("question", e.target.value)}
              label="Question to ask"
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Question type</InputLabel>
              <Select
                label="Question type"
                value={field.questionType || "text"}
                onChange={(e) => onChange("questionType", e.target.value)}
              >
                <MenuItem value="text">Text message</MenuItem>
                <MenuItem value="list">List message</MenuItem>
                <MenuItem value="buttons">Button message (max 3)</MenuItem>
              </Select>
            </FormControl>
            {(field.questionType === "list" || field.questionType === "buttons") && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Options{field.questionType === "buttons" ? " (title ≤ 20 chars)" : ""}
                </Typography>
                {(field.options || []).map((opt, idx) => (
                  <Box key={idx} sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    <TextField
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder="Option text"
                      value={opt.title || ""}
                      inputProps={{ maxLength: field.questionType === "buttons" ? 20 : 24 }}
                      onChange={(e) => {
                        const opts = [...(field.options || [])];
                        opts[idx] = { ...opts[idx], title: e.target.value };
                        onChange("options", opts);
                      }}
                    />
                    <IconButton size="small" onClick={() => onChange("options", (field.options || []).filter((_, i) => i !== idx))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {(field.questionType !== "buttons" || (field.options || []).length < 3) && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => onChange("options", [...(field.options || []), { title: "" }])}
                  >
                    Add option
                  </Button>
                )}
              </Box>
            )}
          </Box>
        )}
        {isFixed && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Data type</InputLabel>
              <Select
                value={field.fixedDataType || "string"}
                label="Data type"
                onChange={(e) => onChange("fixedDataType", e.target.value)}
              >
                {FIXED_DATA_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(field.fixedDataType || "string") === "array" ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  Array items (each can be string, number, or boolean)
                </Typography>
                {(field.fixedArrayValues || []).map((item, idx) => (
                  <Box key={idx} sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    <Select
                      size="small"
                      value={item.type || "string"}
                      onChange={(e) => {
                        const arr = [...(field.fixedArrayValues || [])];
                        arr[idx] = { ...arr[idx], type: e.target.value };
                        onChange("fixedArrayValues", arr);
                      }}
                      sx={{ minWidth: 90 }}
                    >
                      {ARRAY_ITEM_TYPE_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                    <TextField
                      size="small"
                      placeholder={item.type === "number" ? "0" : item.type === "boolean" ? "true/false" : "Value"}
                      value={item.value ?? ""}
                      onChange={(e) => {
                        const arr = [...(field.fixedArrayValues || [])];
                        arr[idx] = { ...arr[idx], value: e.target.value };
                        onChange("fixedArrayValues", arr);
                      }}
                      type={item.type === "number" ? "number" : "text"}
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        const arr = (field.fixedArrayValues || []).filter((_, i) => i !== idx);
                        onChange("fixedArrayValues", arr);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const arr = [...(field.fixedArrayValues || []), { type: "string", value: "" }];
                    onChange("fixedArrayValues", arr);
                  }}
                >
                  Add item
                </Button>
              </>
            ) : (field.fixedDataType || "string") === "boolean" ? (
              <FormControl size="small" fullWidth>
                <Select
                  value={field.fixedValue === "true" || field.fixedValue === true ? "true" : "false"}
                  onChange={(e) => onChange("fixedValue", e.target.value)}
                >
                  <MenuItem value="true">true</MenuItem>
                  <MenuItem value="false">false</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <TextField
                size="small"
                fullWidth
                placeholder={field.fixedDataType === "number" ? "e.g. 100" : "Fixed value"}
                type={field.fixedDataType === "number" ? "number" : "text"}
                value={field.fixedValue ?? ""}
                onChange={(e) => onChange("fixedValue", e.target.value)}
              />
            )}
          </Box>
        )}
        {isGenerated && (
          <FormControl size="small" fullWidth>
            <Select
              value={field.generatedType || "uuid"}
              onChange={(e) => onChange("generatedType", e.target.value)}
            >
              {GENERATED_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </TableCell>
      <TableCell>
        {isAskCustomer && (
          <FormControl size="small" fullWidth>
            <Select
              value={field.validation || ""}
              onChange={(e) => onChange("validation", e.target.value)}
            >
              {VALIDATION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {isCatalog && (
          <FormControl size="small" fullWidth>
            <InputLabel>Output as</InputLabel>
            <Select
              value={
                field.outputDataType !== undefined && field.outputDataType !== null
                  ? field.outputDataType
                  : ORDER_FIELDS.find((f) => f.value === field.sourceKey)?.numeric
                    ? "number"
                    : ""
              }
              label="Output as"
              onChange={(e) => onChange("outputDataType", e.target.value)}
            >
              {OUTPUT_DATA_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || "string"} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {isContact && (
          <FormControl size="small" fullWidth>
            <InputLabel>Output as</InputLabel>
            <Select
              value={field.outputDataType || ""}
              label="Output as"
              onChange={(e) => onChange("outputDataType", e.target.value)}
            >
              {OUTPUT_DATA_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || "string"} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {isTemplateVariable && (
          <FormControl size="small" fullWidth>
            <InputLabel>Output as</InputLabel>
            <Select
              value={field.outputDataType || ""}
              label="Output as"
              onChange={(e) => onChange("outputDataType", e.target.value)}
            >
              {OUTPUT_DATA_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || "string"} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </TableCell>
      {showRemove && (
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={onRemove}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </TableCell>
      )}
    </TableRow>
  );
}

function GroupSection({ group, onChange, onRemove, onAddField, showOrderFields = true, templateVariableFields = null }) {
  const [open, setOpen] = useState(true);
  const fields = group.fields || [];

  const updateGroup = (key, value) => {
    onChange({ ...group, [key]: value });
  };

  const updateField = (index, key, value) => {
    const next = [...fields];
    next[index] = { ...(next[index] || {}), [key]: value };
    updateGroup("fields", next);
  };

  const removeField = (index) => {
    updateGroup("fields", fields.filter((_, i) => i !== index));
  };

  return (
    <Paper variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          bgcolor: "action.hover",
          cursor: "pointer",
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          <Typography variant="subtitle1">
            {group.groupName || "Group"}
          </Typography>
        </Box>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2, pt: 1 }}>
          <TextField
            size="small"
            fullWidth
            label="Group name (e.g. Billing Address)"
            value={group.groupName || ""}
            onChange={(e) => updateGroup("groupName", e.target.value)}
            sx={{ mb: 2 }}
          />
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>API field name</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Value / Question / Map from order</TableCell>
                  <TableCell>Validation</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((f, i) => (
                  <FieldRow
                    key={i}
                    field={f}
                    onChange={(k, v) => updateField(i, k, v)}
                    onRemove={() => removeField(i)}
                    showRemove
                    showOrderFields={showOrderFields}
                    templateVariableFields={templateVariableFields}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddField}
            sx={{ mt: 1 }}
          >
            Add field
          </Button>
        </Box>
      </Collapse>
    </Paper>
  );
}

const SUMMARY_VALUE_TYPE_OPTIONS = [
  { value: "field", label: "From field" },
  { value: "order", label: "From order" },
  { value: "fixed", label: "Fixed value" },
];

const SUMMARY_ORDER_FIELDS = [
  { value: "totalProducts", label: "Total products" },
  { value: "productName", label: "Product name" },
];

function SummaryFieldRow({ row, availableFields, onChange, onRemove, showOrderFields = true }) {
  const isFixed = row.valueType === "fixed";
  const isOrder = row.valueType === "order";
  const filteredSummaryOptions = showOrderFields
    ? SUMMARY_VALUE_TYPE_OPTIONS
    : SUMMARY_VALUE_TYPE_OPTIONS.filter((o) => o.value !== "order");
  return (
    <TableRow>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          label="Label"
          placeholder="e.g. Name"
          value={row.label || ""}
          onChange={(e) => onChange({ ...row, label: e.target.value })}
        />
      </TableCell>
      <TableCell sx={{ minWidth: 150 }}>
        <FormControl size="small" fullWidth>
          <InputLabel>Value type</InputLabel>
          <Select
            label="Value type"
            value={!showOrderFields && (row.valueType || "field") === "order" ? "field" : (row.valueType || "field")}
            onChange={(e) => onChange({ ...row, valueType: e.target.value, fieldPath: "", fixedValue: "", orderField: "" })}
          >
            {filteredSummaryOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell sx={{ minWidth: 200 }}>
        {isFixed ? (
          <TextField
            size="small"
            fullWidth
            placeholder="Fixed text"
            value={row.fixedValue || ""}
            onChange={(e) => onChange({ ...row, fixedValue: e.target.value })}
          />
        ) : isOrder ? (
          <FormControl size="small" fullWidth>
            <Select
              value={row.orderField || ""}
              onChange={(e) => onChange({ ...row, orderField: e.target.value })}
              displayEmpty
            >
              <MenuItem value="">Select…</MenuItem>
              {SUMMARY_ORDER_FIELDS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <FormControl size="small" fullWidth>
            <Select
              value={row.fieldPath || ""}
              onChange={(e) => onChange({ ...row, fieldPath: e.target.value })}
              displayEmpty
            >
              <MenuItem value="">Select field…</MenuItem>
              {availableFields.map((f) => (
                <MenuItem key={f} value={f}>{f}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </TableCell>
      <TableCell padding="checkbox">
        <IconButton size="small" onClick={onRemove} aria-label="Remove row">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

const defaultFormConfig = () => ({
  fields: [],
  lineItemsConfig: {
    catalogArrayPath: "allProducts",
    targetPath: "line_items",
    columns: [],
  },
});

/**
 * Reusable API Integration form (API URL, method, headers, payload fields, line items).
 * Used in Settings page and in Workflow "API Integration" action config.
 * value: { apiUrl, method, headers, formConfig: { fields, lineItemsConfig } }
 * onChange: (newValue) => void
 */
export function ApiIntegrationForm({ value = {}, onChange, showOrderFields = true, templateVariableFields = null }) {
  const apiUrl = value.apiUrl ?? "";
  const method = value.method ?? "POST";
  const headers = value.headers && typeof value.headers === "object" ? value.headers : {};
  const formConfig = value.formConfig && typeof value.formConfig === "object" ? value.formConfig : defaultFormConfig();
  const fields = Array.isArray(formConfig.fields) ? formConfig.fields : [];
  const lineItemsConfig = formConfig.lineItemsConfig && typeof formConfig.lineItemsConfig === "object"
    ? {
        catalogArrayPath: "allProducts",
        targetPath: formConfig.lineItemsConfig.targetPath || "line_items",
        columns: Array.isArray(formConfig.lineItemsConfig.columns) ? formConfig.lineItemsConfig.columns : [],
      }
    : { catalogArrayPath: "allProducts", targetPath: "line_items", columns: [] };
  const summaryConfig = formConfig.summaryConfig && typeof formConfig.summaryConfig === "object"
    ? formConfig.summaryConfig
    : { fields: [] };
  const summaryFields = Array.isArray(summaryConfig.fields) ? summaryConfig.fields : [];

  const availableFieldNames = fields.reduce((acc, f) => {
    if (f.type === "group") {
      (f.fields || []).forEach((gf) => { if (gf.fieldName) acc.push(gf.fieldName); });
    } else if (f.fieldName) {
      acc.push(f.fieldName);
    }
    return acc;
  }, []);

  const hasAskCustomerField = fields.some((f) =>
    f.type === "group" ? (f.fields || []).some((gf) => gf.sourceType === "ask_customer") : f.sourceType === "ask_customer"
  );

  const [headerKey, setHeaderKey] = useState("");
  const [headerValue, setHeaderValue] = useState("");

  const emit = (next) => {
    if (typeof onChange === "function") onChange(next);
  };

  const updateField = (key, val) => {
    emit({ ...value, [key]: val });
  };

  const updateFormConfig = (next) => {
    emit({ ...value, formConfig: next });
  };

  const updateFieldAtIndex = (index, key, val) => {
    const next = [...fields];
    next[index] = { ...(next[index] || {}), [key]: val };
    updateFormConfig({ ...formConfig, fields: next });
  };

  const updateGroupAtIndex = (index, updated) => {
    const next = [...fields];
    next[index] = updated;
    updateFormConfig({ ...formConfig, fields: next });
  };

  const addField = () => {
    updateFormConfig({
      ...formConfig,
      fields: [
        ...fields,
        { fieldName: "", sourceType: showOrderFields ? "catalog" : "ask_customer", sourceKey: "", question: "", validation: "", fixedValue: "", generatedType: "uuid" },
      ],
    });
  };

  const addGroup = () => {
    updateFormConfig({ ...formConfig, fields: [...fields, { type: "group", groupName: "", fields: [] }] });
  };

  const addFieldToGroup = (groupIndex) => {
    const g = fields[groupIndex];
    if (!g || g.type !== "group") return;
    const newFields = [...(g.fields || []), { fieldName: "", sourceType: "catalog", sourceKey: "" }];
    updateGroupAtIndex(groupIndex, { ...g, fields: newFields });
  };

  const removeFieldAtIndex = (index) => {
    updateFormConfig({ ...formConfig, fields: fields.filter((_, i) => i !== index) });
  };

  const addHeader = () => {
    if (headerKey.trim()) {
      updateField("headers", { ...headers, [headerKey.trim()]: headerValue });
      setHeaderKey("");
      setHeaderValue("");
    }
  };

  const removeHeader = (key) => {
    const next = { ...headers };
    delete next[key];
    updateField("headers", next);
  };

  const addLineItemColumn = () => {
    updateFormConfig({
      ...formConfig,
      lineItemsConfig: {
        ...lineItemsConfig,
        columns: [
          ...(lineItemsConfig.columns || []),
          { apiField: "", sourceType: "catalog", mapFrom: "", fixedValue: "", fixedDataType: "string" },
        ],
      },
    });
  };

  const patchLineItemColumn = (index, patch) => {
    const cols = [...(lineItemsConfig.columns || [])];
    cols[index] = { ...(cols[index] || {}), ...patch };
    updateFormConfig({
      ...formConfig,
      lineItemsConfig: { ...lineItemsConfig, columns: cols },
    });
  };

  const removeLineItemColumn = (index) => {
    updateFormConfig({
      ...formConfig,
      lineItemsConfig: {
        ...lineItemsConfig,
        columns: (lineItemsConfig.columns || []).filter((_, i) => i !== index),
      },
    });
  };

  const addSummaryFieldItem = () => {
    updateFormConfig({
      ...formConfig,
      summaryConfig: { ...summaryConfig, fields: [...summaryFields, { label: "", valueType: "field", fieldPath: "" }] },
    });
  };

  const updateSummaryFieldItem = (index, updated) => {
    const next = [...summaryFields];
    next[index] = updated;
    updateFormConfig({ ...formConfig, summaryConfig: { ...summaryConfig, fields: next } });
  };

  const removeSummaryFieldItem = (index) => {
    updateFormConfig({
      ...formConfig,
      summaryConfig: { ...summaryConfig, fields: summaryFields.filter((_, i) => i !== index) },
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>API settings</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <TextField
            label="API URL"
            value={apiUrl}
            onChange={(e) => updateField("apiUrl", e.target.value)}
            placeholder="https://api.example.com/orders"
            fullWidth
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Method</InputLabel>
            <Select value={method} label="Method" onChange={(e) => updateField("method", e.target.value)}>
              <MenuItem value="POST">POST</MenuItem>
              <MenuItem value="PUT">PUT</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Headers</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField size="small" placeholder="Header name" value={headerKey} onChange={(e) => setHeaderKey(e.target.value)} sx={{ width: 160 }} />
          <TextField size="small" placeholder="Value" value={headerValue} onChange={(e) => setHeaderValue(e.target.value)} sx={{ width: 200 }} />
          <Button size="small" variant="outlined" onClick={addHeader}>Add</Button>
        </Box>
        {Object.entries(headers).map(([k, v]) => (
          <Box key={k} sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 120 }}>{k}: {v}</Typography>
            <IconButton size="small" onClick={() => removeHeader(k)}><DeleteIcon fontSize="small" /></IconButton>
          </Box>
        ))}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Payload fields</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" startIcon={<AddIcon />} onClick={addField}>Add field</Button>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addGroup}>Add group</Button>
          </Box>
        </Box>
        {hasAskCustomerField && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formConfig.collectionMode === "single_message"}
                  onChange={(e) =>
                    updateFormConfig({ ...formConfig, collectionMode: e.target.checked ? "single_message" : "one_by_one" })
                  }
                />
              }
              label="Ask all “Ask customer” fields in one message"
            />
            <Typography variant="caption" color="text.secondary" component="div">
              The customer can reply in one message or several. AI fills in the fields from their replies, and after
              30 seconds without a new message they are asked only for what is still missing.
            </Typography>
          </Box>
        )}
        {fields.some((f) => !f.type || f.type !== "group") && (
          <TableContainer sx={{ mb: 2, overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>API field name</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Value / Question / Map from order</TableCell>
                  <TableCell>Validation</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((f, i) =>
                  (!f.type || f.type !== "group") && (
                    <FieldRow key={i} field={f} onChange={(k, v) => updateFieldAtIndex(i, k, v)} onRemove={() => removeFieldAtIndex(i)} showRemove showOrderFields={showOrderFields} templateVariableFields={templateVariableFields} />
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {fields.map((item, i) =>
          item.type === "group" && (
            <GroupSection key={i} group={item} onChange={(updated) => updateGroupAtIndex(i, updated)} onRemove={() => removeFieldAtIndex(i)} onAddField={() => addFieldToGroup(i)} showOrderFields={showOrderFields} templateVariableFields={templateVariableFields} />
          )
        )}
      </Paper>

      {showOrderFields && <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Map order products</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Map each product in the order to your API's line item fields.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <TextField
            size="small"
            label="Array field name in payload"
            value={lineItemsConfig.targetPath || ""}
            onChange={(e) => updateFormConfig({ ...formConfig, lineItemsConfig: { ...lineItemsConfig, targetPath: (e.target.value || "").trim() || "line_items" } })}
            placeholder="e.g. line_items"
            sx={{ minWidth: 280 }}
          />
        </Box>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>API field</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Map from order / Fixed value / Value type</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {(lineItemsConfig.columns || []).map((col, i) => (
                <LineItemColumnRow
                  key={i}
                  col={col}
                  onPatch={(patch) => patchLineItemColumn(i, patch)}
                  onRemove={() => removeLineItemColumn(i)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Button size="small" startIcon={<AddIcon />} onClick={addLineItemColumn} sx={{ mt: 1 }}>Add column</Button>
      </Paper>}

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>Confirmation summary</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addSummaryFieldItem}>Add field</Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define what to show the customer in the confirmation message before they confirm. Leave empty to auto-generate from payload fields.
        </Typography>
        {summaryFields.length > 0 ? (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Label</TableCell>
                  <TableCell>Value type</TableCell>
                  <TableCell>Field / Fixed value</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryFields.map((row, i) => (
                  <SummaryFieldRow
                    key={i}
                    row={row}
                    availableFields={availableFieldNames}
                    onChange={(updated) => updateSummaryFieldItem(i, updated)}
                    onRemove={() => removeSummaryFieldItem(i)}
                    showOrderFields={showOrderFields}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            No custom fields — summary will be auto-generated from collected fields.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}

export default function ApiIntegrationPage() {
  const { accountid, workspaceId } = useParams();
  const navigate = useNavigate();
  const workspaces = useSelector((state) => state.workspace?.workspaces) || [];
  const currentWorkspace = useSelector((state) => state.workspace?.currentWorkspace);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [apiUrl, setApiUrl] = useState("");
  const [method, setMethod] = useState("POST");
  const [headers, setHeaders] = useState({});
  const [headerKey, setHeaderKey] = useState("");
  const [headerValue, setHeaderValue] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [fields, setFields] = useState([]);
  const [lineItemsConfig, setLineItemsConfig] = useState({
    catalogArrayPath: "allProducts",
    targetPath: "line_items",
    columns: [],
  });
  const [summaryConfig, setSummaryConfig] = useState({ fields: [] });
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  const availableFieldNames = useMemo(() => {
    const names = [];
    for (const f of fields) {
      if (f.type === "group") {
        for (const gf of (f.fields || [])) {
          if (gf.fieldName) names.push(gf.fieldName);
        }
      } else if (f.fieldName) {
        names.push(f.fieldName);
      }
    }
    return names;
  }, [fields]);

  const effectiveWorkspaceId = workspaceId || currentWorkspace?._id;

  const fetchIntegration = useCallback(async () => {
    if (!effectiveWorkspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const token = await getAuthToken();
      const res = await fetch(
        `${API_BASE}/api/workspaces/${effectiveWorkspaceId}/api-integration`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load");
      if (data.integration) {
        setApiUrl(data.integration.apiUrl || "");
        setMethod(data.integration.method || "POST");
        setHeaders(
          data.integration.headers && typeof data.integration.headers === "object"
            ? data.integration.headers
            : {}
        );
        setEnabled(Boolean(data.integration.enabled));
        const fc = data.integration.formConfig || {};
        setFields(Array.isArray(fc.fields) ? fc.fields : []);
        setLineItemsConfig(
          fc.lineItemsConfig && typeof fc.lineItemsConfig === "object"
            ? {
                catalogArrayPath: "allProducts",
                targetPath: fc.lineItemsConfig.targetPath || "line_items",
                columns: Array.isArray(fc.lineItemsConfig.columns) ? fc.lineItemsConfig.columns : [],
              }
            : { catalogArrayPath: "allProducts", targetPath: "line_items", columns: [] }
        );
        setSummaryConfig(
          fc.summaryConfig && typeof fc.summaryConfig === "object"
            ? fc.summaryConfig
            : { fields: [] }
        );
      }
    } catch (err) {
      setError(err.message || "Failed to load integration");
    } finally {
      setLoading(false);
    }
  }, [effectiveWorkspaceId]);

  useEffect(() => {
    fetchIntegration();
  }, [fetchIntegration]);

  const fetchPendingOrders = useCallback(async () => {
    if (!effectiveWorkspaceId) return;
    setLoadingOrders(true);
    try {
      const token = await getAuthToken();
      const res = await fetch(
        `${API_BASE}/api/workspaces/${effectiveWorkspaceId}/pending-external-orders`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (res.ok) setPendingOrders(data.pendingOrders || []);
    } catch (e) {
      setPendingOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, [effectiveWorkspaceId]);

  useEffect(() => {
    if (effectiveWorkspaceId && !loading) fetchPendingOrders();
  }, [effectiveWorkspaceId, loading, fetchPendingOrders]);

  const handleSave = async () => {
    if (!effectiveWorkspaceId) {
      setError("Select a workspace first");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const token = await getAuthToken();
      const res = await fetch(
        `${API_BASE}/api/workspaces/${effectiveWorkspaceId}/api-integration`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            apiUrl,
            method,
            headers,
            enabled,
            formConfig: {
              fields,
              lineItemsConfig,
              summaryConfig,
            },
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to save");
      setSuccess("Saved successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const addHeader = () => {
    if (headerKey.trim()) {
      setHeaders((prev) => ({ ...prev, [headerKey.trim()]: headerValue }));
      setHeaderKey("");
      setHeaderValue("");
    }
  };

  const removeHeader = (key) => {
    setHeaders((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const updateFieldAtIndex = (index, key, value) => {
    setFields((prev) => {
      const next = [...prev];
      next[index] = { ...(next[index] || {}), [key]: value };
      return next;
    });
  };

  const updateGroupAtIndex = (index, updated) => {
    setFields((prev) => {
      const next = [...prev];
      next[index] = updated;
      return next;
    });
  };

  const addField = () => {
    setFields((prev) => [
      ...prev,
      {
        fieldName: "",
        sourceType: "catalog",
        sourceKey: "",
        question: "",
        validation: "",
        fixedValue: "",
        generatedType: "uuid",
      },
    ]);
  };

  const addGroup = () => {
    setFields((prev) => [...prev, { type: "group", groupName: "", fields: [] }]);
  };

  const addFieldToGroup = (groupIndex) => {
    const g = fields[groupIndex];
    if (!g || g.type !== "group") return;
    const newFields = [...(g.fields || []), { fieldName: "", sourceType: "catalog", sourceKey: "" }];
    updateGroupAtIndex(groupIndex, { ...g, fields: newFields });
  };

  const removeFieldAtIndex = (index) => {
    setFields((prev) => prev.filter((_, i) => i !== index));
  };

  const addLineItemColumn = () => {
    setLineItemsConfig((prev) => ({
      ...prev,
      columns: [
        ...(prev.columns || []),
        { apiField: "", sourceType: "catalog", mapFrom: "", fixedValue: "", fixedDataType: "string" },
      ],
    }));
  };

  const patchLineItemColumn = (index, patch) => {
    setLineItemsConfig((prev) => {
      const cols = [...(prev.columns || [])];
      cols[index] = { ...(cols[index] || {}), ...patch };
      return { ...prev, columns: cols };
    });
  };

  const removeLineItemColumn = (index) => {
    setLineItemsConfig((prev) => ({
      ...prev,
      columns: (prev.columns || []).filter((_, i) => i !== index),
    }));
  };

  const addSummaryField = () => {
    setSummaryConfig((prev) => ({
      fields: [...(prev.fields || []), { label: "", valueType: "field", fieldPath: "" }],
    }));
  };

  const updateSummaryField = (index, updated) => {
    setSummaryConfig((prev) => {
      const next = [...(prev.fields || [])];
      next[index] = updated;
      return { ...prev, fields: next };
    });
  };

  const removeSummaryField = (index) => {
    setSummaryConfig((prev) => ({
      ...prev,
      fields: (prev.fields || []).filter((_, i) => i !== index),
    }));
  };

  if (!effectiveWorkspaceId && workspaces.length > 0) {
    navigate(`/home/${accountid}/workspaces/${workspaces[0]._id}/api-integration`, { replace: true });
    return null;
  }

  if (!effectiveWorkspaceId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">
          No workspace selected. Switch a workspace from the header.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: "100%", 
      display: "flex", 
      flexDirection: "column", 
      maxWidth: 1000, 
      margin: "0 auto",
      bgcolor: "background.default"
    }}>
      {/* 1. Header Area - Fixed */}
      <Box sx={{ p: 2, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <IconButton size="small" onClick={() => navigate(`/home/${accountid}/settings`)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">API Integration</Typography>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
      </Box>

      {/* 2. Scrollable Content Area */}
      <Box sx={{ 
        flexGrow: 1, 
        overflowY: "auto", 
        px: 2,
        pb: 2,
        /* Custom UI-friendly scrollbar */
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: (theme) => theme.palette.mode === 'dark' ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
          borderRadius: "4px",
          "&:hover": {
            background: (theme) => theme.palette.mode === 'dark' ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
          },
        },
      }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* 1. API Settings */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                API settings
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                <TextField
                  label="API URL"
                  value={apiUrl}
                  onChange={(e) => setApiUrl(e.target.value)}
                  placeholder="https://api.example.com/orders"
                  fullWidth
                  size="small"
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Method</InputLabel>
                  <Select value={method} label="Method" onChange={(e) => setMethod(e.target.value)}>
                    <MenuItem value="POST">POST</MenuItem>
                    <MenuItem value="PUT">PUT</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
                  label="Enabled"
                />
              </Box>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Headers
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                <TextField
                  size="small"
                  placeholder="Header name"
                  value={headerKey}
                  onChange={(e) => setHeaderKey(e.target.value)}
                  sx={{ width: 160 }}
                />
                <TextField
                  size="small"
                  placeholder="Value"
                  value={headerValue}
                  onChange={(e) => setHeaderValue(e.target.value)}
                  sx={{ width: 200 }}
                />
                <Button size="small" variant="outlined" onClick={addHeader}>
                  Add
                </Button>
              </Box>
              {Object.entries(headers).map(([k, v]) => (
                <Box key={k} sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    {k}: {v}
                  </Typography>
                  <IconButton size="small" onClick={() => removeHeader(k)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Paper>

            {/* 2. Visual Payload Builder */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">Payload fields</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button size="small" startIcon={<AddIcon />} onClick={addField}>
                    Add field
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addGroup}>
                    Add group
                  </Button>
                </Box>
              </Box>

              {fields.some((f) => !f.type || f.type !== "group") && (
                <TableContainer sx={{ mb: 2, overflowX: "auto" }}>
                  <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>API field name</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Value / Question / Map from order</TableCell>
                      <TableCell>Validation</TableCell>
                      <TableCell padding="checkbox" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map(
                      (f, i) =>
                        (!f.type || f.type !== "group") && (
                          <FieldRow
                            key={i}
                            field={f}
                            onChange={(k, v) => updateFieldAtIndex(i, k, v)}
                            onRemove={() => removeFieldAtIndex(i)}
                            showRemove
                          />
                        )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {fields.map(
              (item, i) =>
                item.type === "group" && (
                  <GroupSection
                    key={i}
                    group={item}
                    onChange={(updated) => updateGroupAtIndex(i, updated)}
                    onRemove={() => removeFieldAtIndex(i)}
                    onAddField={() => addFieldToGroup(i)}
                  />
                )
            )}
          </Paper>

          {/* 3. Line items mapping */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Map order products
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Map each product in the order to your API’s line item fields. Choose the name of the array field in your payload.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
              <TextField
                size="small"
                label="Array field name in payload"
                value={lineItemsConfig.targetPath || ""}
                onChange={(e) =>
                  setLineItemsConfig((prev) => ({
                    ...prev,
                    targetPath: (e.target.value || "").trim() || "line_items",
                  }))
                }
                placeholder="e.g. line_items, order_items, products"
                helperText="Key name in the JSON where the product list will be sent"
                sx={{ minWidth: 280 }}
              />
            </Box>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>API field</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Map from order / Fixed value</TableCell>
                    <TableCell padding="checkbox" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(lineItemsConfig.columns || []).map((col, i) => (
                    <LineItemColumnRow
                      key={i}
                      col={col}
                      onPatch={(patch) => patchLineItemColumn(i, patch)}
                      onRemove={() => removeLineItemColumn(i)}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Button size="small" startIcon={<AddIcon />} onClick={addLineItemColumn} sx={{ mt: 1 }}>
              Add column
            </Button>
          </Paper>

          {/* 4. Order summary */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="h6">Order summary</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addSummaryField}>
                Add field
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Define what to show the customer in the confirmation message. Leave empty to auto-generate from payload fields.
            </Typography>
            {(summaryConfig.fields || []).length > 0 ? (
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Label</TableCell>
                      <TableCell>Value type</TableCell>
                      <TableCell>Field / Fixed value</TableCell>
                      <TableCell padding="checkbox" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(summaryConfig.fields || []).map((row, i) => (
                      <SummaryFieldRow
                        key={i}
                        row={row}
                        availableFields={availableFieldNames}
                        onChange={(updated) => updateSummaryField(i, updated)}
                        onRemove={() => removeSummaryField(i)}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                No custom fields — summary will be auto-generated.
              </Typography>
            )}
          </Paper>

          {/* Recent orders – payload sent & response */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="h6">
                <HistoryIcon sx={{ verticalAlign: "middle", mr: 0.5 }} />
                Recent orders (payload sent to API)
              </Typography>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={fetchPendingOrders}
                disabled={loadingOrders}
              >
                Refresh
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              After a catalog order is sent, the request and response are listed here so you can see exactly what payload was sent to your API.
            </Typography>
            {loadingOrders ? (
              <Box sx={{ py: 2, textAlign: "center" }}>
                <CircularProgress size={24} />
              </Box>
            ) : pendingOrders.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No orders sent yet. Place a catalog order via WhatsApp to see the payload here.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {pendingOrders.slice(0, 20).map((order) => {
                  const lastLog = order.apiLogs && order.apiLogs.length > 0 ? order.apiLogs[order.apiLogs.length - 1] : null;
                  const contactName = order.contactId?.name || order.contactId?.phoneNumberID || "—";
                  const isExpanded = expandedOrderId === order._id;
                  return (
                    <Paper key={order._id} variant="outlined" sx={{ overflow: "hidden" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 2,
                          py: 1,
                          bgcolor: "action.hover",
                          cursor: "pointer",
                        }}
                        onClick={() => setExpandedOrderId(isExpanded ? null : order._id)}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          <Typography variant="body2">
                            {new Date(order.createdAt).toLocaleString()} · {contactName} · {order.status}
                          </Typography>
                        </Box>
                      </Box>
                      <Collapse in={isExpanded}>
                        <Box sx={{ p: 2 }}>
                          {lastLog && (
                            <>
                              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                                Payload sent (request body)
                              </Typography>
                              <Box
                                component="pre"
                                sx={{
                                  p: 1.5,
                                  bgcolor: "action.hover",
                                  borderRadius: 1,
                                  overflow: "auto",
                                  fontSize: "0.75rem",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-all",
                                }}
                              >
                                {typeof lastLog.requestPayload === "object"
                                  ? JSON.stringify(lastLog.requestPayload, null, 2)
                                  : String(lastLog.requestPayload ?? "—")}
                              </Box>
                              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                                Response (status {lastLog.responseStatus ?? "—"})
                              </Typography>
                              <Box
                                component="pre"
                                sx={{
                                  p: 1.5,
                                  bgcolor: "action.hover",
                                  borderRadius: 1,
                                  overflow: "auto",
                                  fontSize: "0.75rem",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-all",
                                }}
                              >
                                {typeof lastLog.responseBody === "object"
                                  ? JSON.stringify(lastLog.responseBody, null, 2)
                                  : lastLog.errorMessage
                                    ? `Error: ${lastLog.errorMessage}`
                                    : String(lastLog.responseBody ?? "—")}
                              </Box>
                            </>
                          )}
                          {!lastLog && (
                            <Typography variant="body2" color="text.secondary">
                              No API call logged (e.g. still collecting answers).
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>

    {/* 3. Footer Area - Fixed */}
    {!loading && (
      <Box sx={{ 
        p: 2, 
        flexShrink: 0, 
        borderTop: 1, 
        borderColor: 'divider', 
        bgcolor: 'background.paper',
        display: "flex", 
        gap: 2 
      }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={24} /> : "Save"}
        </Button>
        <Button variant="outlined" onClick={() => navigate(`/home/${accountid}/settings`)}>
          Cancel
        </Button>
      </Box>
    )}
  </Box>
);
}
